import ActionForm from '@form/ActionForm/ActionForm';
import FormDivider from '@form/FormDivider';
import FormInput from '@form/FormInput';
import FormWatcher from '@form/FormWatcher';
import { Blockquote, InfoTooltip } from '@ui';
import moment from 'moment';
import { MutableRefObject, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR, { mutate } from 'swr';
import { useClubSlug } from '../../Clubs/hooks';
import Loader from '../../Loader';
import BookersFieldArray from '../BookingAssistant/BookersFieldArray';
import { Trainer } from '../Trainer/types';
import { Allocation, Space } from '../types';
import AllocationCustomPrice from './AllocationCustomPrice';
import AllocationGroupGridInput from './AllocationGroupGridInput';
import ConflictResolutionCards, { ConflictAwareSubmit } from './ConflictResolutionCards';
import ExceptionsFieldArray from './ExceptionsFieldArray';
import { ExceptionItem, ScheduleParams } from './scheduleTypes';
import SubscriptionTemplateInput from './SubscriptionTemplateInput';
import WeekdayInput from './WeekdayInput';

interface Props {
    allocation?: Required<Pick<Allocation, 'id'>> & Partial<Allocation>;
    callback?: (newAllocation: Allocation) => any;
    defaultValues?: Partial<Allocation>;
    journalTemplateId?: number;
    defaultTrainerUserId?: number;
    // Ref populated by ExceptionsFieldArray so the wrapper can inject exceptions from outside
    appendExceptionRef?: MutableRefObject<((value: ExceptionItem) => void) | null>;
    // Called whenever space/time/recurrence fields change so the wrapper can drive the schedule panel
    onScheduleParamsChange?: (params: ScheduleParams | null) => void;
    // Overlaps found by the schedule check; undefined when the form runs without it
    conflictCount?: number;
}

// Watches schedule-relevant fields and reports all selected space IDs plus base params to the parent
const ScheduleParamsWatcher = ({
    onChange,
    isEdit,
    allocation
}: {
    onChange: (params: ScheduleParams | null) => void;
    isEdit: boolean;
    allocation?: Required<Pick<Allocation, 'id'>> & Partial<Allocation>;
}) => {
    const values = useWatch({
        name: ['space_id', 'spaces', 'start', 'end', 'repeat_period', 'repeat_until', 'repeat_weekdays']
    });

    useEffect(() => {
        const [space_id, spaces, start, end, repeat_period, repeat_until, repeat_weekdays] = values;
        // Edit mode uses a single space_id field; create mode uses the spaces[] multi-select
        const space_ids: number[] = isEdit
            ? space_id
                ? [space_id]
                : []
            : ((spaces as number[] | undefined)?.filter(Boolean) ?? []);
        if (!space_ids.length || !start || !end) {
            onChange(null);
            return;
        }
        onChange({
            space_ids,
            start: start instanceof Date ? start.toISOString() : start,
            end: end instanceof Date ? end.toISOString() : end,
            ...(isEdit && allocation?.id != null ? { exclude_id: allocation.id } : {}),
            ...(repeat_period != null ? { repeat_period } : {}),
            ...(repeat_until
                ? { repeat_until: repeat_until instanceof Date ? repeat_until.toISOString() : repeat_until }
                : {}),
            ...(repeat_weekdays?.length ? { repeat_weekdays } : {})
        });
    }, [JSON.stringify(values)]);

    return null;
};

// Form for creating and editing space allocations with support for recurring bookings, custom pricing, and booker management
const AllocationForm = ({
    allocation,
    callback,
    defaultValues,
    journalTemplateId,
    defaultTrainerUserId,
    appendExceptionRef,
    onScheduleParamsChange,
    conflictCount
}: Props) => {
    const { t } = useTranslation('space-manager');
    const clubSlug = useClubSlug();
    const { data: spaceData, isLoading: spaceIsLoading } = useSWR<{
        data: Space[];
    }>(`club/${clubSlug}/space-manager/space`);
    const { data: trainerData, isLoading: trainerIsLoading } = useSWR<{
        data: Trainer[];
    }>(`club/${clubSlug}/space-manager/trainer`);
    const { data: allocationData, isLoading: allocationIsLoading } = useSWR<{ data: Allocation }>(
        allocation?.id ? `space-manager/allocation/${allocation.id}` : null
    );
    // Resolve SpaceManager trainer.id from a template trainer's user_id
    const resolvedDefaultValues = useMemo(() => {
        if (!defaultValues) return defaultValues;
        const trainer = defaultTrainerUserId
            ? trainerData?.data?.find((candidate) => candidate.user_id === defaultTrainerUserId)
            : undefined;
        return {
            ...defaultValues,
            ...(trainer ? { trainer_id: trainer.id } : {})
        };
    }, [defaultValues, defaultTrainerUserId, trainerData?.data]);

    // Memoized to keep object identity stable — Form.tsx calls setValue for every key when defaultValues reference changes
    const formDefaultValues = useMemo(() => {
        if (allocationData !== undefined) {
            // `status` is carried along by the resource but has no field here; sending it back
            // fails validation for open booking requests (pending / awaiting_payment)
            const { status, ...allocationValues } = allocationData.data;
            return {
                ...allocationValues,
                start: new Date(allocationData.data.start),
                end: new Date(allocationData.data.end),
                update_price_meta: false
            };
        }
        return {
            start: moment().startOf('hour').add(1, 'hour').toDate(),
            end: moment().startOf('hour').add(2, 'hour').toDate(),
            is_shadow: false,
            update_price_meta: false,
            conflict_resolution: '',
            ...resolvedDefaultValues,
            spaces: resolvedDefaultValues?.space_id ? [resolvedDefaultValues.space_id] : []
        };
    }, [allocationData, resolvedDefaultValues]);

    return (
        <Loader isLoading={spaceIsLoading || allocationIsLoading || trainerIsLoading}>
            <ActionForm<{ data: Allocation }>
                url={allocation?.id ? `space-manager/allocation/${allocation.id}` : 'space-manager/allocation'}
                onSuccess={(data) => {
                    // the response is a compact resource without `meta`, so a recalculated price
                    // only becomes visible once the cached detail request is refetched
                    if (allocation?.id) {
                        mutate(`space-manager/allocation/${allocation.id}`);
                    }
                    callback(data?.data);
                }}
                method={allocation?.id ? 'PUT' : 'POST'}
                appendedData={journalTemplateId ? { journal_template_id: journalTemplateId } : undefined}
                defaultValues={formDefaultValues}
                renderSubmit={
                    allocation
                        ? undefined
                        : (isLoading) => <ConflictAwareSubmit conflictCount={conflictCount} isLoading={isLoading} />
                }
            >
                {onScheduleParamsChange && (
                    <ScheduleParamsWatcher
                        onChange={onScheduleParamsChange}
                        isEdit={!!allocation}
                        allocation={allocation}
                    />
                )}
                {!allocation && <SubscriptionTemplateInput />}
                {!!allocation ? (
                    <FormInput.Select
                        options={spaceData?.data.map((space) => ({ key: space.id, label: space.name }))}
                        name="space_id"
                        label={t('allocation.form.spaceLabel')}
                    />
                ) : (
                    <FormInput.SelectMultiple
                        options={spaceData?.data.map((space) => ({ key: space.id, label: space.name }))}
                        name="spaces"
                        label={t('allocation.form.spacesLabel')}
                    />
                )}
                <FormInput.Select
                    disableCustomValue
                    options={[
                        { key: '', label: t('allocation.form.noTrainerOption') },
                        ...(trainerData?.data?.map((trainer) => ({
                            key: trainer.id,
                            label: trainer.user?.name || trainer.user?.email || `Trainer #${trainer.id}`
                        })) ?? [])
                    ]}
                    name="trainer_id"
                    label={t('allocation.form.trainerLabel')}
                />
                <FormInput name="title" label={t('allocation.form.titleLabel')} />
                <div className="tw-mb-4 tw-mt-4">
                    <FormInput.Checkbox
                        name="is_shadow"
                        label={
                            <span className="tw-flex tw-items-center tw-gap-2">
                                {t('allocation.form.shadowLabel')}
                                <InfoTooltip tooltip={t('allocation.form.shadowTooltip')} />
                            </span>
                        }
                    />
                </div>
                <FormInput.Textarea name="remarks" label={t('allocation.form.descriptionLabel')} />
                <FormInput.TimeRange startName="start" endName="end" withDate />
                {!allocation && (
                    <FormDivider title={t('allocation.form.recurringSectionTitle')} collapsible>
                        <>
                            <FormInput.Select
                                name="repeat_period"
                                label={t('allocation.form.repeatLabel')}
                                options={[
                                    {
                                        key: null,
                                        label: t('allocation.form.repeatOptions.none')
                                    },
                                    {
                                        key: 1,
                                        label: t('allocation.form.repeatOptions.daily')
                                    },
                                    {
                                        key: 7,
                                        label: t('allocation.form.repeatOptions.weekly')
                                    },
                                    {
                                        key: 14,
                                        label: t('allocation.form.repeatOptions.biweekly')
                                    },
                                    {
                                        key: 31,
                                        label: t('allocation.form.repeatOptions.monthly')
                                    },
                                    {
                                        key: 365,
                                        label: t('allocation.form.repeatOptions.yearly')
                                    }
                                ]}
                                customOption
                                customOptionProps={{
                                    type: 'number',
                                    placeholder: t('allocation.form.repeatCustomPlaceholder')
                                }}
                            />
                            <FormWatcher name="repeat_period">
                                {(value) =>
                                    !!value && (
                                        <>
                                            <FormInput.DatePicker
                                                name="repeat_until"
                                                label={t('allocation.form.repeatUntilLabel')}
                                            />
                                            <Blockquote className="tw-my-4">
                                                {t('allocation.form.weekdayHint')}
                                            </Blockquote>
                                            <WeekdayInput name="repeat_weekdays" />
                                            <ExceptionsFieldArray name="exceptions" appendRef={appendExceptionRef} />
                                        </>
                                    )
                                }
                            </FormWatcher>
                        </>
                    </FormDivider>
                )}
                <FormDivider title={t('allocation.form.groupSectionTitle')} collapsible defaultExpanded>
                    <AllocationGroupGridInput name="allocation_group_id" />
                </FormDivider>
                <FormDivider title={t('allocation.form.playersSectionTitle')} collapsible defaultExpanded>
                    <BookersFieldArray clubSlug={clubSlug} hideSelf />
                </FormDivider>
                <AllocationCustomPrice />
                {allocation?.recurring_id && (
                    <Blockquote color="warning" className="tw-mb-4">
                        {t('allocation.form.recurringEditWarning')}
                    </Blockquote>
                )}
                {allocation?.id && (
                    <Blockquote color="warning" className="tw-mb-4">
                        {t('allocation.form.priceRecalcWarning')}
                    </Blockquote>
                )}
                {/* when creating, this is the only way to get rule-based prices — without it the
                    allocation keeps the subscription-template price or the manually entered one */}
                <FormInput.Checkbox
                    name="update_price_meta"
                    className="tw-mb-4"
                    label={t(
                        allocation?.id ? 'allocation.form.updatePriceLabel' : 'allocation.form.calculatePriceLabel'
                    )}
                />
                {!allocation && <ConflictResolutionCards conflictCount={conflictCount} />}
            </ActionForm>
        </Loader>
    );
};

export default AllocationForm;
